/**
 * Utility functions for date formatting and parsing
 */

/**
 * Formats a date string as "Monday, Jan 7 2025" (timezone-safe)
 * 
 * @param dateString - Date string in format "2025-10-21T15:00:00" or "2025-10-21"
 * @returns Formatted date string or empty string if invalid
 */
export function formatSessionDate(dateString: string | null): string {
  try {
    if (!dateString) return '';
    
    // Parse the date string manually to avoid timezone conversion
    // Expected format: "2025-10-21T15:00:00" or "2025-10-21"
    const datePart = dateString.split('T')[0]; // Get just the date part
    const [year, month, day] = datePart.split('-').map(Number);
    
    // Create date object using local timezone (no timezone conversion)
    const date = new Date(year, month - 1, day); // month is 0-indexed
    
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    };
    const formatted = date.toLocaleDateString('en-US', options);
    // Remove comma between day and year: "Monday, Jan 7, 2025" -> "Monday, Jan 7 2025"
    return formatted.replace(/,(\s+\d{4})$/, '$1');
  } catch (error) {
    return '';
  }
}

/**
 * Formats a time string as "12pm - 1pm" (one hour block)
 * 
 * @param timeString - Time string in format "12:00 PM" or "12pm"
 * @returns Formatted time range string or default if invalid
 */
export function formatSessionTime(timeString: string | null): string {
  try {
    if (!timeString) return '12pm - 1pm';
    
    // Parse the time string (assuming format like "12:00 PM" or "12pm")
    const time = timeString.toLowerCase().replace(/\s+/g, '');
    const timeMatch = time.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)/);
    
    if (!timeMatch) return '12pm - 1pm';
    
    const hour = parseInt(timeMatch[1]);
    const minute = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
    const period = timeMatch[3];
    
    // Create start time
    const startHour = period === 'pm' && hour !== 12 ? hour + 12 : (period === 'am' && hour === 12 ? 0 : hour);
    const startTime = new Date();
    startTime.setHours(startHour, minute, 0, 0);
    
    // Create end time (one hour later)
    const endTime = new Date(startTime);
    endTime.setHours(endTime.getHours() + 1);
    
    // Format both times
    const formatTime = (date: Date) => {
      const hour = date.getHours();
      const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
      const period = hour < 12 ? 'am' : 'pm';
      return `${displayHour}${period}`;
    };
    
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  } catch (error) {
    return '12pm - 1pm';
  }
}
